<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { MultiSelect, type MultiSelectObjectOption } from '@collector/shared-ui'
import { injectSportsApiClient } from '@collector/shared-ui-domain'
import { isLettersAndSpaces, isNumericInteger } from '@collector/shared-utils'
import type { Participants } from '@collector/sportsapi-client'
import {
  ParticipantProviderId,
  type BaseParticipant,
} from '@collector/sportsapi-client-legacy'
import type { SubParticipant } from '@collector/sportsapi-types'

import type { LineupConfig } from '../../../../interfaces/LineupConfig'
import type { LineupModel } from '../../../../interfaces/LineupModel'
import AddToPositionButtonGroup from '../../../common/AddToPositionButtonGroup.vue'
import ShirtNrTextInput from '../../../common/ShirtNrTextInput.vue'
import FoundPlayerOption from './FoundPlayerOption.vue'

type Props = {
  config: LineupConfig
  lineup: LineupModel
}
const props = defineProps<Props>()

type Emits = {
  add: [positionKey: PropertyKey, player: Partial<SubParticipant>]
}
const emit = defineEmits<Emits>()

//composables
const sportsApiClient = injectSportsApiClient()

// state
const shirtNr = ref('')
const searchTerm = ref('')
const selectedPlayerId = ref<number | undefined>()
const selectedPlayer = ref<BaseParticipant | null>(null)
const open = ref(false)
const options = ref<
  MultiSelectObjectOption[] | Promise<MultiSelectObjectOption[]>
>([])

// computed
const subType = computed(() => selectedPlayer.value?.details?.subtype || null)

const buttonsDisabled = computed(() => {
  if (!selectedPlayerId.value) return { disabled: true }
  else {
    const idExists = Object.values(props.lineup)
      .flat()
      .some((p) => p.id === selectedPlayerId.value)
    if (idExists)
      return {
        disabled: true,
        reasonText: `Player with id ${selectedPlayerId.value} is already added`,
      }
  }
  return { disabled: false }
})

const placeholder = computed(() => {
  return props.config.addPlayer.searchPlaceholder ?? 'Player name or ID'
})

// methods
async function searchForPlayer(term: string): Promise<void> {
  const searchTermValue = term.trim()
  const shouldFetchById =
    isNumericInteger(searchTermValue) && Number(searchTermValue) > 0
  const shouldFetchByName =
    isLettersAndSpaces(searchTermValue) && searchTermValue.length > 2

  if (shouldFetchById) {
    options.value = fetchSelectOptions({
      provider_id: ParticipantProviderId.Scoresway,
      provider_participant_id: Number(searchTermValue),
    })
    open.value = true
  } else if (shouldFetchByName) {
    options.value = fetchSelectOptions({ short_name: searchTermValue })
    open.value = true
  } else {
    options.value = []
    open.value = false
  }
}

async function fetchParticipants(
  query: Partial<Participants.ParticipantsGetQuery>,
): Promise<BaseParticipant[]> {
  const participantsResponse = await sportsApiClient.Participants.get({
    type: 'person',
    gender: props.config.addPlayer.gender,
    sport_id: props.config.addPlayer.sportId,
    ...query,
  })
  const participants = participantsResponse.api.data.participants || []

  const filteredParticipants = participants.filter(
    (p) => p.details.subtype !== 'referee' && p.details.subtype !== 'director',
  )

  return filteredParticipants
}

async function fetchSelectOptions(
  query: Partial<Participants.ParticipantsGetQuery>,
): Promise<MultiSelectObjectOption[]> {
  const participants = await fetchParticipants(query)
  return mapParticipantsToSelectOptions(participants)
}

function mapParticipantsToSelectOptions(
  participants: BaseParticipant[],
): MultiSelectObjectOption[] {
  return participants.map((participant) => ({
    value: participant.id,
    name: participant.short_name,
    metadata: participant,
  }))
}

async function handleAdd(positionKey: PropertyKey): Promise<void> {
  if (selectedPlayer.value) {
    emit('add', positionKey, {
      ...selectedPlayer.value,
      shirt_nr: shirtNr.value,
    })
    clear()
  } else {
    throw new Error('Cannot find player in options')
  }
}

function clear(): void {
  shirtNr.value = ''
  searchTerm.value = ''
  selectedPlayerId.value = undefined
}

// watch
watch(searchTerm, searchForPlayer)

watch(selectedPlayerId, async (id) => {
  selectedPlayer.value = null
  const resolvedOptions = await options.value
  if (Array.isArray(resolvedOptions)) {
    const selectedOption = resolvedOptions.find((o) => o.value === id)
    if (selectedOption)
      selectedPlayer.value = selectedOption.metadata as BaseParticipant
  }
})
</script>

<template>
  <div class="bg-neutral-light-7 flex gap-1 rounded-md p-2 pb-5.5">
    <ShirtNrTextInput v-model="shirtNr" />
    <MultiSelect
      v-model="selectedPlayerId"
      v-model:searchTerm="searchTerm"
      v-model:open="open"
      class="w-full"
      :options
      withClear
      mode="combobox"
      :placeholder
    >
      <template #displayedValue="{ selectedOption }">
        <FoundPlayerOption
          v-if="typeof selectedOption === 'object' && selectedOption"
          :option="selectedOption"
        />
      </template>
      <template #option="{ option }">
        <FoundPlayerOption
          v-if="typeof option === 'object' && option"
          :option
        />
      </template>
    </MultiSelect>
    <AddToPositionButtonGroup
      type="found"
      :config
      :lineup
      :shirtNr
      :subType
      :disabled="buttonsDisabled.disabled"
      :disabledReasonText="buttonsDisabled.reasonText"
      @add="handleAdd"
    />
  </div>
</template>
